// CrmSkeleton — the CRM's loading state.
//
// Replaces a centred spinner on a blank field, which appeared in 17 places
// across the app. A lone spinner says only "wait": it gives the eye nothing to
// settle on, no sense of what is coming, and no clue how much. The arrival is
// then a jump from an empty page to a dense one, which reads as a flash rather
// than a load.
//
// These render the SHAPE of the content instead — a header band, then the cards
// or rows that are about to land — so the layout is already correct when the
// data arrives and only the detail fades in. Same reason the quote detail page
// was given a skeleton of itself rather than a spinner.
//
// The sheen comes from `.crm-shimmer` in crm.css. Note its keyframe is
// `crmSheen`, NOT `crm-shimmer` — that name was already taken by an unrelated
// translateX animation, and reusing it silently applies the wrong one.

/** One shimmering block. `w`/`h` are Tailwind classes so callers stay declarative. */
function Bar({
  w = 'w-full',
  h = 'h-4',
  className = '',
  delay = 0,
}: {
  w?: string;
  h?: string;
  className?: string;
  delay?: number;
}): JSX.Element {
  return (
    <span
      className={`crm-shimmer block rounded ${w} ${h} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

function Card({ children, delay = 0 }: { children: React.ReactNode; delay?: number }): JSX.Element {
  return (
    <div
      className="crm-rise rounded-[14px] border border-[color:var(--border-1)] bg-[color:var(--surface-0,#fff)] p-4"
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/**
 * Announce the wait to assistive tech exactly once per skeleton.
 *
 * A spinner with no accessible name is invisible to a screen reader, so the
 * page simply went quiet; and repeating the announcement per block would read
 * the word "loading" a dozen times.
 */
function LoadingAnnounce({ label }: { label: string }): JSX.Element {
  return (
    <span className="sr-only" role="status" aria-live="polite">
      {label}
    </span>
  );
}

/**
 * A record page: identity header, then a two-column body.
 *
 * Mirrors the anatomy every CRM detail page shares, so the skeleton is the same
 * shape as what replaces it regardless of which record is loading.
 */
export function CrmDetailSkeleton({
  label = 'Loading',
  'data-testid': testId = 'crm-detail-skeleton',
}: {
  label?: string;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid={testId}>
      <LoadingAnnounce label={label} />

      <Card>
        <div className="flex items-center gap-3">
          <Bar w="w-11" h="h-11" className="!rounded-full" />
          <div className="flex-1 space-y-2">
            <Bar w="w-1/3" h="h-4" delay={60} />
            <Bar w="w-1/4" h="h-3" delay={120} />
          </div>
          <Bar w="w-24" h="h-7" className="!rounded-full" delay={180} />
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1].map((c) => (
          <Card key={c} delay={140 + c * 90}>
            <Bar w="w-1/3" h="h-3.5" delay={c * 90} />
            <div className="mt-4 space-y-2.5">
              {[0, 1, 2, 3].map((r) => (
                <div key={r} className="flex items-center justify-between gap-4">
                  <Bar w="w-1/3" h="h-3" delay={r * 70} />
                  <Bar w="w-1/4" h="h-3" delay={r * 70 + 40} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * A table: header band then `rows` striped lines.
 *
 * Deliberately NOT a spinner in a table shell — the shell already implies rows,
 * so a spinner in the middle of it reads as the table having failed to fill.
 */
export function CrmTableSkeleton({
  rows = 6,
  label = 'Loading',
  'data-testid': testId = 'crm-table-skeleton',
}: {
  rows?: number;
  label?: string;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <LoadingAnnounce label={label} />
      <Bar w="w-full" h="h-9" className="!rounded-[8px]" />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4 py-1">
          <Bar w="w-1/5" h="h-3.5" delay={i * 70} />
          <Bar w="w-24" h="h-5" className="!rounded-full" delay={i * 70 + 30} />
          <Bar w="w-1/6" h="h-3.5" delay={i * 70 + 60} />
          <Bar w="w-1/5" h="h-3.5" delay={i * 70 + 90} />
          <span className="flex-1" />
          <Bar w="w-7" h="h-7" className="!rounded-full" delay={i * 70 + 120} />
        </div>
      ))}
    </div>
  );
}

/**
 * A panel's worth of content — for a card mid-page rather than a whole route.
 *
 * `lines` so a caller can match the block it is standing in for; a four-line
 * skeleton in front of a one-line panel makes the page shrink on load.
 */
export function CrmPanelSkeleton({
  lines = 3,
  label = 'Loading',
  'data-testid': testId = 'crm-panel-skeleton',
}: {
  lines?: number;
  label?: string;
  'data-testid'?: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2.5 py-1" data-testid={testId}>
      <LoadingAnnounce label={label} />
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <Bar w="w-1/3" h="h-3.5" delay={i * 80} />
          <Bar w="w-1/5" h="h-3.5" delay={i * 80 + 40} />
        </div>
      ))}
    </div>
  );
}
